import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface ConfirmationDialogProps {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly variant?: 'warning' | 'info' | 'error';
}

/**
 * Reusable confirmation dialog content.
 * Use with BaseModal for a complete confirmation dialog experience.
 *
 * Example:
 * ```tsx
 * <BaseModal open={isOpen} onClose={handleClose}>
 *   <ConfirmationDialog
 *     title="Delete Item"
 *     message="Are you sure you want to delete this item?"
 *     onConfirm={handleDelete}
 *     onCancel={handleClose}
 *     variant="warning"
 *   />
 * </BaseModal>
 * ```
 */
export function ConfirmationDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'info',
}: ConfirmationDialogProps) {
  const getButtonColor = () => {
    switch (variant) {
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button variant="contained" color={getButtonColor()} onClick={onConfirm}>
          {confirmLabel}
        </Button>
      </Stack>
    </Stack>
  );
}

export default ConfirmationDialog;
